import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, CreditCard, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { identify, trackStartedCheckout } from "../utils/klaviyo";
import { setFlag, HAS_ORDERED_FLAG } from "../utils/preferences";

// Base URL of the Node.js middleware on Render (no trailing path)
const MIDDLEWARE_URL = (process.env.REACT_APP_MIDDLEWARE_URL || "https://krunchmate-middleware.onrender.com").replace(/\/+$/, "");
const STRIPE_CHECKOUT_ORIGIN = "https://checkout.stripe.com/";
const ORDER_SUCCESS_PATH = "/checkout?status=success";
const GENERIC_CHECKOUT_ERROR = "There was an issue processing your order. Please try again.";
const NETWORK_ERROR = "We couldn't reach our payment server. Please check your connection and try again.";

// Zero-total orders come back with a direct redirect; only ever follow it to this site's checkout success page
const toSafeSuccessPath = (value) => {
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin === window.location.origin && url.pathname === "/checkout" && url.searchParams.get("status") === "success") {
      return `${url.pathname}${url.search}`;
    }
  } catch {
    // fall through to the default success path
  }
  return ORDER_SUCCESS_PATH;
};

// localStorage can throw in private browsing or with site data blocked
const readStorage = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};
const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // the form still works; the draft just won't survive a refresh
  }
};
const removeStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // nothing stored to remove
  }
};

const GUEST_ADDRESS_KEY = "km_guest_address";
const GUEST_ADDRESS_FIELDS = ["firstName", "lastName", "address", "city", "postcode"];

// Only plain, short strings are restored, so a tampered or stale draft can't break the form
const readGuestAddress = () => {
  try {
    const parsed = JSON.parse(readStorage(GUEST_ADDRESS_KEY) || "{}");
    const draft = {};
    for (const key of GUEST_ADDRESS_FIELDS) {
      if (typeof parsed?.[key] === "string" && parsed[key].length <= 200) draft[key] = parsed[key];
    }
    return draft;
  } catch {
    return {};
  }
};

const formatPounds = (pence) => `£${(pence / 100).toFixed(2)}`;

// Only show server messages that are plain, short strings
const safeMessage = (value, fallback) =>
  typeof value === "string" && value.length > 0 && value.length <= 200 ? value : fallback;

async function postJson(path, body) {
  const response = await fetch(`${MIDDLEWARE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, data: data && typeof data === "object" ? data : {} };
}

export default function Checkout() {
  const { detailed, subtotal, shippingFee, grandTotal, clear } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderPlaced = searchParams.get("status") === "success";
  const paymentCancelled = searchParams.get("status") === "cancelled";

  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  // Signed in = a stored JWT; the address endpoint identifies the customer from it
  const { isLoggedIn: isSignedIn } = useAuth();

  // Every order needs explicit agreement to the current Terms & Conditions (the middleware checks it too)
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [formData, setFormData] = useState(() => ({
    email: readStorage("km_user_email") || readStorage("km_lead_email"),
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
    // Guests get back what they typed before an accidental refresh
    ...(isSignedIn ? {} : readGuestAddress()),
  }));

  // Back from Stripe (or a £0 order): the cart has become an order, and the draft address is no longer needed
  useEffect(() => {
    if (!orderPlaced) return;
    clear();
    removeStorage(GUEST_ADDRESS_KEY);
    // A customer now, so the footer stops offering the first-order discount
    setFlag(HAS_ORDERED_FLAG);
  }, [orderPlaced, clear]);

  // Klaviyo: arriving here with a basket is the "Started Checkout" that abandoned-cart flows start from.
  // Runs once per visit; the email field identifies the shopper later if they type it in.
  useEffect(() => {
    if (orderPlaced || detailed.length === 0) return;
    identify(formData.email);
    trackStartedCheckout(detailed, subtotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guests: keep the address draft in this browser while they fill the form in
  useEffect(() => {
    if (isSignedIn || orderPlaced) return;
    const draft = {};
    for (const key of GUEST_ADDRESS_FIELDS) draft[key] = formData[key];
    writeStorage(GUEST_ADDRESS_KEY, JSON.stringify(draft));
  }, [isSignedIn, orderPlaced, formData]);

  // Signed-in customers get their saved WooCommerce address; anything already typed is kept
  useEffect(() => {
    const token = readStorage("km_jwt_token");
    const userEmail = readStorage("km_user_email");
    if (!isSignedIn || !token || orderPlaced) return undefined;
    let isCurrent = true;

    const emailQuery = userEmail ? `?email=${encodeURIComponent(userEmail)}` : "";
    fetch(`${MIDDLEWARE_URL}/api/account/address${emailQuery}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const saved = data?.address;
        if (!isCurrent || !saved || typeof saved !== "object") return;
        setFormData((current) => {
          const next = { ...current };
          for (const key of ["firstName", "lastName", "address", "city", "postcode"]) {
            if (!next[key] && typeof saved[key] === "string") next[key] = saved[key];
          }
          return next;
        });
      })
      .catch(() => {
        // pre-filling is a convenience; the form still works empty
      });

    return () => {
      isCurrent = false;
    };
  }, [isSignedIn, orderPlaced]);

  // The server prices everything itself, so it only ever receives product IDs and quantities
  const cartPayload = useMemo(
    () =>
      detailed
        .filter((it) => it.qty > 0 && Number.isInteger(it.wooId))
        .map((it) => ({ wooId: it.wooId, qty: it.qty })),
    [detailed]
  );
  const cartSignature = useMemo(() => JSON.stringify(cartPayload), [cartPayload]);

  // A quote is only valid for the exact cart it was calculated against
  const activeCoupon = appliedCoupon && appliedCoupon.cartSignature === cartSignature ? appliedCoupon : null;

  const displaySubtotal = activeCoupon ? activeCoupon.subtotalPence / 100 : subtotal;
  const displayShipping = activeCoupon ? activeCoupon.shippingPence / 100 : shippingFee;
  const displayTotal = activeCoupon ? activeCoupon.totalPence / 100 : grandTotal;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApplyCoupon = async () => {
    const code = couponInput.trim();
    if (!code || isApplyingCoupon) return;

    setIsApplyingCoupon(true);
    setCouponError("");

    try {
      const { ok, data } = await postJson("/api/validate-coupon", {
        items: cartPayload,
        couponCode: code,
        ...(formData.email.trim() ? { email: formData.email.trim() } : {}),
      });

      const quoteIsWellFormed =
        data.valid === true &&
        typeof data.couponCode === "string" &&
        ["subtotalPence", "discountPence", "shippingPence", "totalPence"].every((key) => Number.isInteger(data[key]));

      if (ok && quoteIsWellFormed) {
        setAppliedCoupon({
          code: data.couponCode,
          subtotalPence: data.subtotalPence,
          discountPence: data.discountPence,
          shippingPence: data.shippingPence,
          totalPence: data.totalPence,
          cartSignature,
        });
        setCouponInput("");
      } else {
        setAppliedCoupon(null);
        setCouponError(safeMessage(data.error, "This discount code isn't valid for your order."));
      }
    } catch (err) {
      console.error("Coupon validation error:", err);
      setCouponError("We couldn't check that code right now. Please try again.");
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError("");
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (isProcessing || cartPayload.length === 0) return;
    if (agreedToTerms !== true) {
      setCheckoutError("Please agree to the Terms & Conditions to place your order.");
      return;
    }

    setIsProcessing(true);
    setCheckoutError("");

    try {
      const { ok, data } = await postJson("/api/checkout", {
        items: cartPayload,
        customerDetails: {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          city: formData.city,
          postcode: formData.postcode,
          country: formData.country,
        },
        ...(activeCoupon ? { couponCode: activeCoupon.code } : {}),
        termsAccepted: agreedToTerms,
      });

      if (ok && data.success === true) {
        // Only ever leave the site for Stripe's own checkout domain
        if (typeof data.checkoutUrl === "string" && data.checkoutUrl.startsWith(STRIPE_CHECKOUT_ORIGIN)) {
          window.location.href = data.checkoutUrl;
          return;
        }
        // Fully discounted order: already placed server-side, no payment step
        if (data.checkoutUrl === null && typeof data.directRedirectUrl === "string") {
          navigate(toSafeSuccessPath(data.directRedirectUrl));
          setIsProcessing(false);
          return;
        }
      }

      if (data.code === "COUPON_REJECTED") {
        setAppliedCoupon(null);
        setCouponError(safeMessage(data.error, "This discount code isn't valid for your order."));
      }
      setCheckoutError(safeMessage(data.error, GENERIC_CHECKOUT_ERROR));
    } catch (err) {
      console.error("Checkout error:", err);
      setCheckoutError(NETWORK_ERROR);
    }
    setIsProcessing(false);
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-[#082D33] text-[#FFFBE5] pt-32 pb-16 flex flex-col items-center px-5 text-center">
        <CheckCircle2 size={48} className="text-[#F6A81E] mb-6" />
        <h1 className="text-3xl lg:text-4xl font-display uppercase tracking-[0.06em] text-[#F6A81E] mb-4">
          Thank You!
        </h1>
        <p className="text-lg opacity-80 max-w-md mb-8" role="status">
          Your order has been placed. We'll email your confirmation shortly.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/shop" className="px-6 py-3 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold uppercase tracking-wider hover:bg-[#f7b43b] transition-colors">
            Keep Shopping
          </Link>
          <Link to="/account" className="px-6 py-3 bg-white/10 rounded-full font-bold uppercase tracking-wider hover:bg-white/20 transition-colors">
            My Account
          </Link>
        </div>
      </div>
    );
  }

  if (detailed.length === 0) {
    return (
      <div className="min-h-screen bg-[#082D33] text-[#FFFBE5] pt-32 flex flex-col items-center px-5">
        <p className="text-xl mb-6">Your cart is empty.</p>
        <Link to="/shop" className="text-[#F6A81E] hover:underline">Return to Shop</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#082D33] text-[#FFFBE5] pt-24 pb-16 px-5 lg:px-10">
      <div className="max-w-6xl mx-auto">
        <Link to="/cart" className="inline-flex items-center gap-2 text-sm font-semibold opacity-70 hover:opacity-100 hover:text-[#F6A81E] transition-colors mb-8">
          <ArrowLeft size={16} /> Back to Cart
        </Link>

        <h1 className="text-3xl lg:text-4xl font-display uppercase tracking-[0.06em] text-[#F6A81E] mb-10">
          Checkout
        </h1>

        {paymentCancelled && (
          <div className="mb-8 p-4 bg-[#FFFBE5]/5 border border-[#FFFBE5]/20 rounded-xl text-sm" role="status">
            Payment was cancelled. Your cart has been saved, so you can pick up where you left off.
          </div>
        )}

        {checkoutError && (
          <div className="mb-8 p-4 bg-red-500/15 border border-red-500/40 rounded-xl text-sm text-red-100 flex items-start gap-3" role="alert">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <span>{checkoutError}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

          {/* Left Column: Form */}
          <div className="lg:col-span-7 space-y-10">
            {/* Rewards Sign-In Banner (guests only) */}
            {!isSignedIn && (
              <div className="mb-8 p-4 bg-[#F6A81E]/10 border border-[#F6A81E]/30 rounded-xl flex justify-between items-center">
                <p className="text-sm text-[#FFFBE5]/90">
                  <span className="font-bold text-[#F6A81E]">✨ Want to earn Krunch points?</span> Sign in for rewards and exclusive perks.
                </p>
                <Link to="/account" className="text-sm font-bold text-[#F6A81E] hover:underline whitespace-nowrap ml-4">
                  Sign In
                </Link>
              </div>
            )}

            <form id="checkout-form" onSubmit={handlePlaceOrder} className="space-y-8">

              {/* Contact Info */}
              <section>
                <h2 className="font-display uppercase tracking-widest text-lg mb-4 flex items-center justify-between">
                  Contact Information
                </h2>
                <div>
                  <label className="block text-xs uppercase tracking-widest opacity-60 mb-2">Email Address</label>
                  <input
                    required type="email" name="email" value={formData.email} onChange={handleChange}
                    onBlur={() => identify(formData.email)}
                    maxLength={254} autoComplete="email"
                    className="w-full p-3 bg-black/20 border border-[#FFFBE5]/20 rounded-xl focus:border-[#F6A81E] focus:outline-none transition-colors text-sm"
                  />
                </div>
              </section>

              {/* Shipping Details */}
              <section>
                <h2 className="font-display uppercase tracking-widest text-lg mb-4">Shipping Address</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest opacity-60 mb-2">First Name</label>
                      <input required type="text" name="firstName" value={formData.firstName} onChange={handleChange} maxLength={50} autoComplete="given-name" className="w-full p-3 bg-black/20 border border-[#FFFBE5]/20 rounded-xl focus:border-[#F6A81E] focus:outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest opacity-60 mb-2">Last Name</label>
                      <input required type="text" name="lastName" value={formData.lastName} onChange={handleChange} maxLength={50} autoComplete="family-name" className="w-full p-3 bg-black/20 border border-[#FFFBE5]/20 rounded-xl focus:border-[#F6A81E] focus:outline-none text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest opacity-60 mb-2">Street Address</label>
                    <input required type="text" name="address" value={formData.address} onChange={handleChange} maxLength={200} autoComplete="street-address" className="w-full p-3 bg-black/20 border border-[#FFFBE5]/20 rounded-xl focus:border-[#F6A81E] focus:outline-none text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest opacity-60 mb-2">City</label>
                      <input required type="text" name="city" value={formData.city} onChange={handleChange} maxLength={100} autoComplete="address-level2" className="w-full p-3 bg-black/20 border border-[#FFFBE5]/20 rounded-xl focus:border-[#F6A81E] focus:outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest opacity-60 mb-2">Postcode</label>
                      <input required type="text" name="postcode" value={formData.postcode} onChange={handleChange} maxLength={10} autoComplete="postal-code" className="w-full p-3 bg-black/20 border border-[#FFFBE5]/20 rounded-xl focus:border-[#F6A81E] focus:outline-none text-sm" />
                    </div>
                  </div>
                </div>
              </section>

              {/* Payment Section */}
              <section>
                <h2 className="font-display uppercase tracking-widest text-lg mb-4 flex items-center gap-2">
                  Payment <Lock size={16} className="opacity-60" />
                </h2>
                <div className="p-6 bg-black/20 border border-[#FFFBE5]/20 rounded-xl text-center space-y-3">
                  <CreditCard size={32} className="mx-auto opacity-50" />
                  <p className="text-sm opacity-80">
                    You'll be taken to Stripe's secure checkout to pay by card, Apple Pay or Google Pay.
                  </p>
                </div>
              </section>
            </form>
          </div>

          {/* Right Column: Order Summary */}
          <div className="lg:col-span-5">
            <div className="bg-[#FFFBE5]/5 border border-[#FFFBE5]/15 rounded-2xl p-6 lg:p-8 sticky top-32">
              <h2 className="font-display uppercase tracking-widest text-lg mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto pr-2">
                {detailed.map((item) => (
                  <div key={item.id} className="flex items-center gap-4">
                    <div className="relative w-16 h-16 bg-black/20 rounded-lg flex items-center justify-center shrink-0">
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#F6A81E] text-[#1D2528] rounded-full text-[10px] font-bold flex items-center justify-center z-10">
                        {item.qty}
                      </span>
                      {item.flavour?.front && (
                        <img src={item.flavour.front} alt={item.flavour.name} className="w-12 h-12 object-contain drop-shadow-md" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-sm leading-tight">{item.flavour?.name}</p>
                      <p className="text-xs opacity-60 mt-0.5">£{item.unitPrice.toFixed(2)} each</p>
                    </div>
                    <p className="font-bold text-sm">£{item.lineTotal.toFixed(2)}</p>
                  </div>
                ))}
              </div>

              {/* Discount Code */}
              <div className="py-5 border-t border-[#FFFBE5]/15">
                {activeCoupon ? (
                  <div className="flex items-center justify-between gap-3 p-3 bg-[#F6A81E]/10 border border-[#F6A81E]/30 rounded-xl text-sm">
                    <span>
                      Code <span className="font-bold uppercase text-[#F6A81E]">{activeCoupon.code}</span> applied
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      className="text-[#FFFBE5]/70 hover:text-[#F6A81E] transition-colors"
                      aria-label="Remove discount code"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleApplyCoupon();
                        }
                      }}
                      maxLength={40}
                      placeholder="Discount code"
                      aria-label="Discount code"
                      className="flex-1 min-w-0 bg-black/20 border border-[#FFFBE5]/20 rounded-xl px-4 py-2.5 text-sm text-[#FFFBE5] placeholder-[#FFFBE5]/50 focus:outline-none focus:border-[#F6A81E] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={isApplyingCoupon || !couponInput.trim()}
                      className="bg-[#FFFBE5]/10 hover:bg-[#FFFBE5]/20 text-[#FFFBE5] px-5 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isApplyingCoupon ? "Checking..." : "Apply"}
                    </button>
                  </div>
                )}
                {couponError && (
                  <p className="mt-2 text-xs text-red-300" role="alert">{couponError}</p>
                )}
              </div>

              <div className="space-y-3 pt-6 border-t border-[#FFFBE5]/15 text-sm">
                <div className="flex justify-between opacity-80">
                  <span>Subtotal</span>
                  <span>£{displaySubtotal.toFixed(2)}</span>
                </div>
                {activeCoupon && activeCoupon.discountPence > 0 && (
                  <div className="flex justify-between text-[#F6A81E]">
                    <span>Discount</span>
                    <span>-{formatPounds(activeCoupon.discountPence)}</span>
                  </div>
                )}
                <div className="flex justify-between opacity-80">
                  <span>Shipping</span>
                  <span>{displayShipping === 0 ? "Free" : `£${displayShipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between font-bold text-lg text-[#F6A81E] pt-3 mt-3 border-t border-[#FFFBE5]/15">
                  <span>Total</span>
                  <span>£{displayTotal.toFixed(2)}</span>
                </div>
              </div>

              {checkoutError && (
                <p className="mt-6 text-sm text-red-300 text-center">{checkoutError}</p>
              )}

              <label className="mt-8 flex items-start gap-3 text-sm cursor-pointer" data-testid="checkout-terms">
                <input
                  type="checkbox"
                  form="checkout-form"
                  required
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (e.target.checked) setCheckoutError("");
                  }}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-[#F6A81E] cursor-pointer"
                  data-testid="checkout-terms-checkbox"
                />
                <span className="opacity-90">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#F6A81E] underline">
                    Terms & Conditions
                  </a>
                  <span className="sr-only"> (opens in a new tab)</span>.
                </span>
              </label>

              <button
                type="submit"
                form="checkout-form"
                disabled={isProcessing || !agreedToTerms}
                className="w-full mt-5 bg-[#F6A81E] text-[#1D2528] py-4 rounded-full font-bold uppercase tracking-widest hover:bg-[#F6A81E]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? "Processing..." : displayTotal === 0 ? "Place Order" : `Pay £${displayTotal.toFixed(2)}`}
                {!isProcessing && <Lock size={16} />}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
